export const PANEL_CSS = `
:host {
  all: initial;
  display: block;
  position: fixed;
  inset: 0;
  pointer-events: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--agent-text);

  --agent-bg: #ffffff;
  --agent-surface: #f6f7f9;
  --agent-surface-2: #eceef2;
  --agent-border: #dfe3ea;
  --agent-border-strong: #c3cad6;
  --agent-text: #14181f;
  --agent-muted: #59636f;
  --agent-subtle: #7b8695;
  --agent-accent: #3b5bdb;
  --agent-accent-hover: #3150c4;
  --agent-accent-soft: #eaefff;
  --agent-accent-text: #ffffff;
  --agent-danger: #c92a2a;
  --agent-danger-soft: #fdecec;
  --agent-warn: #a35200;
  --agent-warn-soft: #fff4e5;
  --agent-ok: #157347;
  --agent-ok-soft: #e8f6ee;
  --agent-focus: #1c46d9;
  --agent-radius: 10px;
  --agent-radius-sm: 6px;
  --agent-shadow: -8px 0 32px rgba(16, 20, 28, 0.12);
  --agent-ease: cubic-bezier(0.16, 1, 0.3, 1);
}

:host([data-theme='dark']) {
  --agent-bg: #14171d;
  --agent-surface: #1b1f26;
  --agent-surface-2: #242932;
  --agent-border: #2c323c;
  --agent-border-strong: #414956;
  --agent-text: #eef1f6;
  --agent-muted: #a7b1bf;
  --agent-subtle: #8b95a4;
  --agent-accent: #8ba2ff;
  --agent-accent-hover: #a3b5ff;
  --agent-accent-soft: #1e2740;
  --agent-accent-text: #0b0e13;
  --agent-danger: #ff9b9b;
  --agent-danger-soft: #2f1c1e;
  --agent-warn: #ffc178;
  --agent-warn-soft: #2e2317;
  --agent-ok: #6fd79b;
  --agent-ok-soft: #14261c;
  --agent-focus: #a3b5ff;
  --agent-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
}

*, *::before, *::after { box-sizing: border-box; }
[hidden] { display: none !important; }
button, input, textarea { font: inherit; color: inherit; margin: 0; }
button { background: none; border: 0; cursor: pointer; }
p, h1, h2, h3, ul, ol { margin: 0; padding: 0; }
ul, ol { list-style: none; }
:focus-visible { outline: 2px solid var(--agent-focus); outline-offset: 2px; border-radius: var(--agent-radius-sm); }

.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0;
}

.launcher {
  position: fixed; right: 20px; bottom: 20px;
  display: inline-flex; align-items: center; gap: 8px;
  min-height: 44px; padding: 0 18px;
  border-radius: 999px;
  background: var(--agent-accent); color: var(--agent-accent-text);
  font-weight: 600; pointer-events: auto;
  box-shadow: 0 12px 40px rgba(16, 20, 28, 0.18);
  transition: transform 200ms var(--agent-ease), background-color 120ms;
}
.launcher:hover { background: var(--agent-accent-hover); transform: translateY(-1px); }

.panel {
  position: fixed; top: 0; right: 0; bottom: 0;
  display: flex; flex-direction: column;
  background: var(--agent-bg);
  border-left: 1px solid var(--agent-border);
  box-shadow: var(--agent-shadow);
  transform: translateX(100%);
  transition: transform 300ms var(--agent-ease);
  pointer-events: auto;
  contain: layout paint;
}
.panel[data-open='true'] { transform: translateX(0); }

.header {
  display: flex; align-items: center; gap: 12px;
  min-height: 56px; padding: 0 12px 0 16px;
  border-bottom: 1px solid var(--agent-border);
}
.header__mark {
  display: grid; place-items: center; width: 28px; height: 28px; flex: none;
  border-radius: var(--agent-radius-sm);
  background: var(--agent-accent-soft); color: var(--agent-accent);
  font-size: 12px; font-weight: 700;
}
.header__titles { flex: 1; min-width: 0; }
.header__title { font-size: 15px; font-weight: 650; letter-spacing: -0.01em; }
.header__sub { font-size: 11px; color: var(--agent-subtle); }
.header__state { font-size: 11px; }
.header__state[data-state='open'] { color: var(--agent-ok); }
.header__state[data-state='connecting'] { color: var(--agent-warn); }
.header__state[data-state='closed'] { color: var(--agent-danger); }

.iconbtn {
  display: grid; place-items: center; width: 32px; height: 32px; flex: none;
  border-radius: var(--agent-radius-sm); color: var(--agent-muted);
}
.iconbtn:hover { background: var(--agent-surface-2); color: var(--agent-text); }

.stream {
  flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
  padding: 16px; display: flex; flex-direction: column; gap: 12px;
}

.stream > * { flex: 0 0 auto; }

.block { animation: rise 200ms var(--agent-ease) both; }
.block[data-restored] { animation: none; }

.empty {
  margin: auto 0; display: flex; flex-direction: column; align-items: center;
  gap: 6px; padding: 24px 8px; text-align: center;
}
.empty__mark {
  display: grid; place-items: center; width: 40px; height: 40px; margin-bottom: 2px;
  border-radius: 50%; background: var(--agent-accent-soft); color: var(--agent-accent);
  font-size: 18px;
}
.empty__title { font-size: 14px; font-weight: 600; }
.empty__body { font-size: 12px; line-height: 1.5; color: var(--agent-muted); max-width: 30ch; }

.msg { display: flex; flex-direction: column; gap: 4px; }
.msg__bubble {
  padding: 10px 12px; border-radius: var(--agent-radius);
  white-space: pre-wrap; overflow-wrap: anywhere;
}
.msg[data-role='user'] { align-items: flex-end; }
.msg[data-role='user'] .msg__bubble {
  background: var(--agent-accent); color: var(--agent-accent-text);
  border-bottom-right-radius: var(--agent-radius-sm); max-width: 88%;
}
.msg[data-role='agent'] .msg__bubble {
  background: var(--agent-surface); border: 1px solid var(--agent-border);
  border-bottom-left-radius: var(--agent-radius-sm);
}

.suggestions { display: flex; flex-direction: column; gap: 8px; }
.suggestion {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%; padding: 10px 12px; text-align: left;
  border: 1px solid var(--agent-border); border-radius: var(--agent-radius);
  background: var(--agent-bg); color: var(--agent-text);
  transition: border-color 120ms, background-color 120ms, transform 120ms;
}
.suggestion:hover {
  border-color: var(--agent-accent); background: var(--agent-accent-soft);
  transform: translateX(2px);
}

.status {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px; border-radius: var(--agent-radius);
  border: 1px solid var(--agent-border); background: var(--agent-surface);
  font-size: 12px; color: var(--agent-muted);
}
.status[data-tone='error'] {
  border-color: var(--agent-danger);
  background: var(--agent-danger-soft);
  color: var(--agent-text);
}

.dots { display: inline-flex; gap: 3px; }
.dots i { width: 5px; height: 5px; border-radius: 50%; background: var(--agent-accent); animation: bounce 1.1s infinite; }
.dots i:nth-child(2) { animation-delay: 0.15s; }
.dots i:nth-child(3) { animation-delay: 0.3s; }

.approval {
  border: 1px solid var(--agent-border); border-radius: 14px;
  background: var(--agent-bg); overflow: hidden;
}
.approval[data-tier='WRITE'] { border-color: var(--agent-warn); }
.approval[data-tier='RED'] { border-color: var(--agent-danger); }
.approval__head {
  display: flex; align-items: center; gap: 8px; padding: 12px;
  background: var(--agent-surface); border-bottom: 1px solid var(--agent-border);
  font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--agent-muted);
}
.approval__body { padding: 12px; display: flex; flex-direction: column; gap: 10px; }
.approval__desc { font-weight: 550; overflow-wrap: anywhere; }
.approval__actions { display: flex; gap: 8px; flex-wrap: wrap; }
.approval__resolved { font-size: 12px; color: var(--agent-muted); }

.badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 650; white-space: nowrap;
  border: 1px solid transparent;
}
.badge[data-tier='READ'] { background: var(--agent-ok-soft); color: var(--agent-ok); border-color: var(--agent-ok); }
.badge[data-tier='WRITE'] { background: var(--agent-warn-soft); color: var(--agent-warn); border-color: var(--agent-warn); }
.badge[data-tier='RED'] { background: var(--agent-danger-soft); color: var(--agent-danger); border-color: var(--agent-danger); }

.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-height: 32px; padding: 0 12px;
  border-radius: var(--agent-radius-sm); border: 1px solid transparent;
  font-size: 12px; font-weight: 600; white-space: nowrap;
  transition: background-color 120ms, border-color 120ms, transform 120ms;
}
.btn:active:not(:disabled) { transform: scale(0.97); }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn[data-variant='primary'] { background: var(--agent-accent); color: var(--agent-accent-text); }
.btn[data-variant='primary']:hover:not(:disabled) { background: var(--agent-accent-hover); }
.btn[data-variant='danger'] { background: var(--agent-danger); color: var(--agent-bg); }
.btn[data-variant='secondary'] { background: var(--agent-bg); border-color: var(--agent-border-strong); }
.btn[data-variant='secondary']:hover:not(:disabled) { background: var(--agent-surface); }
.btn[data-variant='ghost'] { color: var(--agent-muted); }
.btn[data-variant='ghost']:hover:not(:disabled) { background: var(--agent-surface); color: var(--agent-text); }

.composer { border-top: 1px solid var(--agent-border); padding: 12px; display: flex; flex-direction: column; gap: 8px; }
.composer__row {
  display: flex; align-items: flex-end; gap: 8px; padding: 8px;
  border: 1px solid var(--agent-border-strong); border-radius: var(--agent-radius);
  transition: border-color 120ms;
}
.composer__row:focus-within { border-color: var(--agent-accent); box-shadow: 0 0 0 3px var(--agent-accent-soft); }
.composer__input {
  flex: 1; min-height: 24px; max-height: 132px; padding: 0 2px;
  border: 0; background: transparent; resize: none; outline: none; line-height: 1.5;
}
.composer__input::placeholder { color: var(--agent-subtle); }
.composer__send {
  display: grid; place-items: center; width: 32px; height: 32px; flex: none;
  border-radius: var(--agent-radius-sm);
  background: var(--agent-accent); color: var(--agent-accent-text);
}
.composer__send:disabled { opacity: 0.4; cursor: not-allowed; }
.composer__hint { display: flex; justify-content: space-between; font-size: 11px; color: var(--agent-subtle); }

.resize {
  position: absolute; top: 0; left: -3px; width: 8px; height: 100%;
  cursor: col-resize; background: transparent; touch-action: none;
  transition: background-color 120ms;
}
.resize:hover, .resize:focus-visible, .resize[data-dragging='true'] { background: var(--agent-accent); }

.mark {
  position: fixed; z-index: 2147483000;
  display: grid; place-items: center;
  min-width: 18px; height: 18px; padding: 0 4px;
  border-radius: 4px;
  background: var(--agent-accent); color: var(--agent-accent-text);
  font-size: 10px; font-weight: 700; line-height: 1;
  pointer-events: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}
.mark__ring {
  position: fixed; z-index: 2147482999;
  border: 1px dashed var(--agent-accent); border-radius: 4px;
  pointer-events: none;
}

.target {
  position: fixed; z-index: 2147483001;
  border: 2px solid var(--agent-accent); border-radius: var(--agent-radius-sm);
  box-shadow: 0 0 0 4px var(--agent-accent-soft), 0 0 0 9999px rgba(16, 20, 28, 0.28);
  pointer-events: none;
  animation: ring 300ms var(--agent-ease) both;
}
.target__tip {
  position: absolute; left: 0; bottom: calc(100% + 8px);
  max-width: 280px; width: max-content; padding: 6px 10px; border-radius: var(--agent-radius-sm);
  background: var(--agent-text); color: var(--agent-bg);
  font-size: 12px; font-weight: 550; white-space: normal;
}
.target[data-flip='true'] .target__tip { bottom: auto; top: calc(100% + 8px); }
.target[data-align='right'] .target__tip { left: auto; right: 0; }

@keyframes rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes bounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.55; } 30% { transform: translateY(-4px); opacity: 1; } }
@keyframes ring { from { opacity: 0; transform: scale(1.04); } to { opacity: 1; transform: none; } }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }
}
`;
